import { useEffect, useState, type ReactNode } from "react";
import { Wordmark } from "./Chrome";
import { PublicFooter } from "./PublicLayout";
import { PUBLIC_BRAND as LIVE, publicBrandStatus as backdropStatus } from "../api/brand";

const LENS_IMG =
  "https://images.unsplash.com/photo-1533746228171-962520811097?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400";

interface AuthLayoutProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  children: ReactNode;
  testId: string;
  backdrop?: "crew" | "lead";
}

const SIGNIN = `${process.env.PUBLIC_URL}/brand/signin/stadium`;
const SLOT = { crew: "signin_background", lead: "admin_signin_background" } as const;
const POS = "object-[45%_50%] lg:object-[50%_55%]";
const srcset = (ext: string) => [768, 1280, 1920].map((w) => `${SIGNIN}-${w}.${ext} ${w}w`).join(", ");


function SignInBackdrop({ kind, onLive }: { kind: "crew" | "lead"; onLive: () => void }) {
  const [live, setLive] = useState(false);
  const [exists, setExists] = useState(false);
  useEffect(() => { let on = true; backdropStatus().then((s) => on && setExists(Boolean(s[SLOT[kind]]))); return () => { on = false; }; }, [kind]);
  const url = `${LIVE}/${SLOT[kind]}`;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden data-testid={kind === "crew" ? "signin-stadium-backdrop" : "signin-lead-backdrop"}>
      {kind === "crew" && (
        <picture>
          <source type="image/webp" srcSet={srcset("webp")} sizes="100vw" />
          <img src={`${SIGNIN}-1280.jpg`} srcSet={srcset("jpg")} sizes="100vw" alt="" fetchPriority="high" className={`h-full w-full object-cover ${POS}`} />
        </picture>
      )}
      {exists && <img src={`${url}?w=1920`} srcSet={`${url}?w=960 960w, ${url}?w=1920 1920w`} sizes="100vw" alt=""
        onLoad={() => { setLive(true); onLive(); }} data-testid={`signin-backdrop-live-${kind}`}
        className={`absolute inset-0 h-full w-full object-cover ${POS} transition-opacity duration-500 motion-reduce:transition-none ${live ? "opacity-100" : "opacity-0"}`} />}
      {(kind === "crew" || live) && <div className="stadium-overlay absolute inset-0" />}
    </div>
  );
}

function LensBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden data-testid="signin-default-backdrop">
      <img src={LENS_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/50" />
    </div>
  );
}

export function AuthLayout({ eyebrow, title, subtitle, children, testId, backdrop }: AuthLayoutProps) {
  const [leadLive, setLeadLive] = useState(false);
  const stadium = backdrop === "crew" || (backdrop === "lead" && leadLive);
  return (
    <div>
      <div data-testid={testId} className="relative min-h-[calc(100vh-var(--banner-h))] overflow-hidden">
        {!stadium && <LensBackdrop />}
        {backdrop && <SignInBackdrop kind={backdrop} onLive={() => setLeadLive(true)} />}
        <div className="pointer-events-none absolute inset-0 grain" aria-hidden />
        <main className="relative flex min-h-[calc(100vh-var(--banner-h))] items-center justify-center px-4 py-10 sm:px-8 sm:py-16">
          <div className="glass-raised w-full max-w-md animate-rise rounded-[24px] p-7 sm:p-10">
            <div className="mb-8 flex justify-center" data-testid="auth-card-logo">
              <Wordmark size="lg" />
            </div>
            <p className="label-caps text-signal">{eyebrow}</p>
            <h1 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">{title}</h1>
            <p className="mt-3 text-sm text-zinc-400">{subtitle}</p>
            <div className="mt-8">{children}</div>
          </div>
        </main>
      </div>
      <PublicFooter />
    </div>
  );
}
